import React, { useEffect, useState } from 'react';
import { adminAgreementAPI } from '../services/api';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useNavigate } from 'react-router-dom';

interface AgreementRow {
  id: number;
  title: string;
  slug: string;
  version: number;
  is_active: boolean;
  force_resign: boolean;
  role_mapping: string[];
  created_at: string;
}

const AdminAgreements: React.FC = () => {
  const [rows, setRows] = useState<AgreementRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAgreementAPI.list();
      setRows(res.data.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const askDelete = (id: number) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setDeleteId(null);
  };

  const handleDelete = async () => {
    if (deleteId == null) return;
    await adminAgreementAPI.delete(deleteId);
    handleConfirmClose();
    load();
  };

  return (
    <Box>
      <Card>
        <CardHeader
          title="Agreements"
          action={
            <Stack direction="row" spacing={1}>
              <IconButton onClick={load} title="Refresh">
                <RefreshIcon />
              </IconButton>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin/agreements/new')}>
                New Agreement
              </Button>
            </Stack>
          }
        />
        <Divider />
        <CardContent>
          {loading ? (
            <Typography>Loading...</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Force Re-sign</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>{r.title}</TableCell>
                    <TableCell>{r.slug}</TableCell>
                    <TableCell>{r.version}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {r.role_mapping?.map((role) => (
                          <Chip key={role} size="small" label={role} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.is_active ? 'Active' : 'Inactive'} color={r.is_active ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.force_resign ? 'Yes' : 'No'} color={r.force_resign ? 'warning' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => navigate(`/admin/agreements/${r.id}`)} title="Edit">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => askDelete(r.id)} title="Delete">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={handleConfirmClose}>
        <DialogTitle>Delete Agreement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this agreement? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmClose}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAgreements;
